import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function BestTacosInVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "best-tacos-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || {};
  const ref_data = (articles || []).find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "La Taqueria Pinche Taco Shop",
    "section-2": "Tacofino (Gastown Taco Bar)",
    "section-3": "Los Cuervos Taqueria & Cantina",
    "section-4": "Sal y Limón",
    "section-5": "Alimentaria Mexicana",
    "section-6": "La Mezcaleria",
    "section-7": "Chancho Tortilleria",
    "section-8": "The Burrow (Formerly Bandidas Taqueria)",
    "section-9": "The Pawn Shop YVR – Taco & Tequila Bar",
    faq: "FAQ",
    "local-regulations": "Local Regulations / Best Practices",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com",
      label: "Destination Vancouver – Official Tourism Site",
    },
    {
      url: "https://streetfoodapp.com/vancouver",
      label: "Street Food App (Vancouver)",
    },
    {
      url: "https://www.lataqueria.com",
      label: "La Taqueria – Official Website",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // We'll match the IDs to the sections for each place, ensuring lat/long are correct.
  const locations = [
    {
      id: "section-1",
      lat: 49.2630,
      lng: -123.1115,
      popupText: "La Taqueria Pinche Taco Shop",
    },
    {
      id: "section-2",
      lat: 49.2830,
      lng: -123.1045,
      popupText: "Tacofino (Gastown Taco Bar)",
    },
    {
      id: "section-3",
      lat: 49.2568,
      lng: -123.0960,
      popupText: "Los Cuervos Taqueria & Cantina",
    },
    {
      id: "section-4",
      lat: 49.2565,
      lng: -123.0899,
      popupText: "Sal y Limón",
    },
    {
      id: "section-5",
      lat: 49.2703,
      lng: -123.1340,
      popupText: "Alimentaria Mexicana",
    },
    {
      id: "section-6",
      lat: 49.2697,
      lng: -123.0693,
      popupText: "La Mezcaleria",
    },
    {
      id: "section-7",
      lat: 49.2635,
      lng: -123.0698,
      popupText: "Chancho Tortilleria",
    },
    {
      id: "section-8",
      lat: 49.2568,
      lng: -123.0700,
      popupText: "The Burrow (Formerly Bandidas Taqueria)",
    },
    {
      id: "section-9",
      lat: 49.2773,
      lng: -123.1260,
      popupText: "The Pawn Shop YVR – Taco & Tequila Bar",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Best Tacos in Vancouver</title>
        <meta
          name="description"
          content="Discover the top taco spots in Vancouver, from authentic Mexican taquerías to modern fusion and seafood specialties."
        />
        <meta
          name="keywords"
          content="vancouver tacos, best tacos vancouver, taco guide vancouver"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/best-tacos-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Best Tacos in Vancouver"
        />
        <meta
          property="og:description"
          content="A thorough guide to Vancouver's best taco spots, complete with tips, FAQs, and local regulations."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/best-tacos-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/best-tacos-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Best Tacos in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Explore Vancouver's delicious taco scene, from traditional to fusion and vegan options."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/best-tacos-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/best-tacos-in-vancouver.jpg"
              }
              alt="A selection of delicious tacos in Vancouver."
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s love affair with tacos runs deep. This west coast city
              boasts a vibrant taco scene that reflects its multicultural flair
              and coastal bounty. From traditional Mexican taquerías to creative
              fusion eateries and seafood-centric stands, Vancouver offers taco
              lovers an exciting range of options. Whether you crave no-frills
              street tacos loaded with cilantro and onion or a gourmet creation
              with locally caught fish and bold sauces, you’ll find a spot here
              to satisfy your taste buds. In this guide, we’ll explore some of
              the best taco destinations in Vancouver, along with practical tips
              on local regulations, FAQs, and resources to help you navigate
              your taco journey.
            </p>

            {/* SECTION 1: La Taqueria Pinche Taco Shop */}
            <h2 id="section-1">La Taqueria Pinche Taco Shop</h2>
            <p ref={(el) => setSpotRef("section-1", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location: </strong>
              <Marker
                coordY={49.2630}
                coordX={-123.1115}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – <em>2450 Yukon St, Vancouver, BC V5Y 0A4</em>
            </p>
            <p>
              La Taqueria is known for its authentic Mexico City–style street
              tacos, served on fresh corn tortillas. Must-try fillings include
              al pastor (spiced pork), carnitas (braised pork), and carne asada
              (grilled beef), all topped with traditional cilantro and onion. A
              self-serve salsa bar lets you customize the heat level to your
              preference. Tacos range from about $3–$5 each, making this a
              budget-friendly option that doesn’t skimp on flavor. Vegetarian
              and vegan choices are also available, thanks to hearty mushroom
              and bean fillings. Operating hours vary by location, but you can
              expect them to open around lunchtime and stay open through
              dinner. Pro tip: Try their horchata for a refreshing, sweet
              counterpart to spicy tacos.

            </p>

            {/* SECTION 2: Tacofino (Gastown Taco Bar) */}
            <h2 id="section-2">Tacofino (Gastown Taco Bar)</h2>
            <p ref={(el) => setSpotRef("section-2", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location: </strong>
              <Marker
                coordY={49.2830}
                coordX={-123.1045}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – <em>15 W Cordova St, Vancouver, BC V6B 1C8</em>
            </p>
            <p>
              Tacofino has become a local institution for West Coast–style fish
              tacos. Originally founded in Tofino as a food truck, Tacofino
              brings a laid-back, surf-town vibe to its Gastown location. Their
              beer-battered fish tacos are legendary, featuring crisp Pacific
              fish, cabbage slaw, and a chipotle mayo. The menu also includes
              crispy chicken, vegetarian yam tacos, burritos, and more. With a
              full bar and late hours, you can enjoy craft cocktails alongside
              your tacos. Prices hover around $6–$7 per taco. Expect a
              bustling atmosphere, especially during peak dinner times and
              weekends.
            </p>

            {/* SECTION 3: Los Cuervos Taqueria & Cantina */}
            <h2 id="section-3">Los Cuervos Taqueria & Cantina</h2>
            <p ref={(el) => setSpotRef("section-3", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location: </strong>
              <Marker
                coordY={49.2568}
                coordX={-123.0960}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – <em>603 Kingsway, Vancouver, BC V5T 3K5</em>
            </p>
            <p>
              Los Cuervos Taqueria captures the spirit of Mexican street food in
              a casual, cantina-style setting. You’ll find plenty of taco
              varieties, from tinga de pollo (spicy pulled chicken) to cochinita
              pibil (Yucatán-style pork). Seafood fans can opt for the seared
              tuna taco, and brunch lovers can drop by on weekends for Mexican
              breakfast specialties. Tacos start at about $4, and they offer a
              robust selection of margaritas, mezcal, and tequila. The vibe is
              lively, colorful, and perfect for a laid-back meal with friends.
            </p>

            {/* SECTION 4: Sal y Limón */}
            <h2 id="section-4">Sal y Limón</h2>
            <p ref={(el) => setSpotRef("section-4", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location: </strong>
              <Marker
                coordY={49.2565}
                coordX={-123.0899}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – <em>Unit 5 – 701 Kingsway, Vancouver, BC V5T 3K6</em>
            </p>
            <p>
              Known for its affordability and extensive menu, Sal y Limón is a
              favorite among families, students, and longtime locals. Tacos here
              cost about $3.50 each, and fillings range from carnitas to chicken
              mole and fish. A highlight is their salsa bar, with options from
              mild avocado salsa to extra-spicy varieties. They also serve
              burritos, quesadillas, tortas, and more. The restaurant’s casual
              environment and friendly service make it an excellent lunch or
              dinner spot, and their hours typically run from late morning to
              evening.
            </p>

            {/* SECTION 5: Alimentaria Mexicana */}
            <h2 id="section-5">Alimentaria Mexicana</h2>
            <p ref={(el) => setSpotRef("section-5", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location: </strong>
              <Marker
                coordY={49.2703}
                coordX={-123.1340}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – <em>1596 Johnston St, Vancouver, BC V6H 3R9</em>
            </p>
            <p>
              Situated on Granville Island, Alimentaria Mexicana goes beyond
              standard tacos by offering a farm-to-table cantina experience. The
              chefs emphasize traditional techniques, and you can often see
              tortillas made on-site. Expect gourmet tacos in the $7–$8 range,
              along with shareable plates featuring fresh, local produce.
              Whether you’re enjoying their cauliflower taco, a robust birria,
              or sampling mezcal flights, Alimentaria delivers a refined take on
              Mexican cuisine. The adjacent market sells imported ingredients
              and stone-ground masa if you feel inspired to cook at home.
            </p>

            {/* SECTION 6: La Mezcaleria */}
            <h2 id="section-6">La Mezcaleria</h2>
            <p ref={(el) => setSpotRef("section-6", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location: </strong>
              <Marker
                coordY={49.2697}
                coordX={-123.0693}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – <em>1622 Commercial Drive, Vancouver, BC V5L 3Y4</em>
            </p>
            <p>
              La Mezcaleria is more of an upscale dinner destination known for
              both its innovative tacos and extensive mezcal and tequila lists.
              Standout menu items include the queso fundido (molten cheese
              starter) and creative taco fillings like hongos con kale. Tacos
              average around $4.50 each but are often sold in pairs or platters.
              The environment is lively and trendy, perfect for a night out with
              friends. Be sure to try one of their signature mezcal cocktails if
              you’re a fan of smoky agave flavors.
            </p>

            {/* SECTION 7: Chancho Tortilleria */}
            <h2 id="section-7">Chancho Tortilleria</h2>
            <p ref={(el) => setSpotRef("section-7", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location: </strong>
              <Marker
                coordY={49.2635}
                coordX={-123.0698}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – <em>2096 Commercial Drive, Vancouver, BC V5N 4B2</em>
            </p>
            <p>
              If carnitas are your passion, Chancho Tortilleria is the place to
              go. They specialize in one thing: slow-cooked pork tacos made with
              nixtamalized corn tortillas. Order by weight—quarter, half, or
              full kilo—and get a stack of warm tortillas to build your own
              tacos. The vibrant space is casual and hip, and while they often
              feature a vegetarian option, this spot is best for dedicated pork
              lovers. Prices are moderate, but the portions are generous, making
              this an excellent group meal.
            </p>

            {/* SECTION 8: The Burrow (Formerly Bandidas Taqueria) */}
            <h2 id="section-8">The Burrow (Formerly Bandidas Taqueria)</h2>
            <p ref={(el) => setSpotRef("section-8", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location: </strong>
              <Marker
                coordY={49.2568}
                coordX={-123.0700}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – <em>2781 Commercial Drive, Vancouver, BC V5N 4C5</em>
            </p>
            <p>
              This vegetarian-centric restaurant offers hearty and creative
              plant-based tacos that satisfy even the most devout carnivores.
              Try the Ronny Russell Taco with roasted yams and guacamole or the
              Leona Gayle Taco with veggie ground “beef.” Almost everything can
              be made vegan by substituting cheese and crema. Tacos generally
              cost around $5 each, but they come generously stuffed. On weekends,
              enjoy a Mexican-inspired brunch with items like eggs or tofu bennys
              on homemade cornbread. Friendly service and a cozy atmosphere
              round out the experience.
            </p>

            {/* SECTION 9: The Pawn Shop YVR – Taco & Tequila Bar */}
            <h2 id="section-9">The Pawn Shop YVR – Taco & Tequila Bar</h2>
            <p ref={(el) => setSpotRef("section-9", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location: </strong>
              <Marker
                coordY={49.2773}
                coordX={-123.1260}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – <em>1117 Granville St, Vancouver, BC V6Z 1M1</em>
            </p>
            <p>
              Known for its late-night hours and party vibe, The Pawn Shop YVR
              is the go-to spot for creative tacos well into the evening. Think
              $2–$5 tacos with inventive fillings like Korean beef or a Big Mac–style
              twist. The bar offers an extensive tequila selection, and the music
              is often pumping. If you’re looking for a lively evening and cheap
              eats in the downtown entertainment district, this is the place to
              be. Happy hour specials can make tacos even more wallet-friendly.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Where can I find the most authentic Mexican tacos?</h3>
              <p>
                Chancho Tortilleria and La Taqueria are top choices for a taste
                that’s close to Mexico City or Michoacán. Both use traditional
                cooking methods and fillings that highlight classic Mexican
                flavors.
              </p>

              <h3>2. What’s the best late-night taco spot?</h3>
              <p>
                The Pawn Shop YVR stands out for its extended hours, downtown
                location, and lively atmosphere. If you want to grab tacos after
                midnight, it’s one of the few that stay open until 1am.
              </p>

              <h3>3. Are there vegan or vegetarian taco options?</h3>
              <p>
                Absolutely. The Burrow (formerly Bandidas Taqueria) is a fully
                vegetarian restaurant, and most of Vancouver’s taquerías, such
                as La Taqueria and Sal y Limón, offer multiple plant-based
                choices. You’ll also find vegan-friendly twists at Tacofino and
                other fusion spots.
              </p>
            </div>

            {/* SECTION: Local Regulations / Best Practices */}
            <h2 id="local-regulations">Local Regulations / Best Practices</h2>
            <p>
              Vancouver’s food scene is governed by Vancouver Coastal Health,
              ensuring high standards of cleanliness and safety at all
              restaurants and food trucks. Remember these tips:
            </p>
            <ul>
              <li>
                <strong>Food Trucks:</strong> Must display a valid permit.
                Consult local apps like Street Food App to find their current
                locations. Proper waste disposal is expected, so use trash and
                recycling bins.
              </li>
              <li>
                <strong>Tipping Etiquette:</strong> It’s customary to tip
                15–20% in restaurants. For counter service and food trucks,
                tipping is optional but appreciated.
              </li>
              <li>
                <strong>Salsa Bars:</strong> Start with small samples to gauge
                the spice level. Some salsas can be quite hot.
              </li>
              <li>
                <strong>Allergies/Dietary Needs:</strong> Inform staff if you
                have allergies or restrictions. Many spots offer gluten-free
                corn tortillas and can accommodate different diets.
              </li>
            </ul>
            <p>
              Always check official resources if you have specific health or
              safety concerns. Some areas may have restrictions on alcohol
              consumption in public spaces, so enjoy any to-go tacos without
              open liquor unless you’re in a designated zone.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s taco landscape is as diverse as its population, with
              options ranging from budget-friendly street tacos to upscale
              gourmet offerings. Whether you’re a devoted carnitas lover or
              looking for plant-based creations, you’ll find a spot that hits
              the mark. Explore these neighborhoods, follow your nose (and the
              lines), and savor the fusion of Pacific Northwest ingredients with
              time-honored Mexican traditions. ¡Buen provecho!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}